import { motion } from "framer-motion";
import { HelpCircle, Plus, Minus } from "lucide-react";
import { useState } from "react";

/**
 * Ashvolt Preorder FAQ.
 * Custom accordion (single-open) so it stays 100% on-brand with the
 * dark/gold cinematic style — no shadcn primitives fighting our tokens.
 */
export default function PreorderFAQ({ data }) {
  const [openId, setOpenId] = useState(null);

  if (!data || !data.items?.length) return null;

  return (
    <section
      id="faq"
      data-testid="preorder-faq"
      className="relative py-24 md:py-32 px-6 md:px-12 lg:px-16"
    >
      <div className="max-w-[1200px] mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          {/* LEFT — sticky heading */}
          <div className="lg:col-span-4 lg:sticky lg:top-32 self-start">
            <div className="flex items-center gap-3 mb-6">
              <span className="relative flex items-center justify-center w-10 h-10 border border-accent/50 bg-black/40">
                <HelpCircle size={16} className="text-accent" />
              </span>
              <span className="font-body text-[10px] uppercase tracking-[0.35em] text-accent">
                {data.eyebrow}
              </span>
            </div>
            <h2 className="font-heading uppercase text-white text-4xl md:text-5xl lg:text-6xl leading-[0.9] mb-6">
              {data.title}
            </h2>
            <p className="font-body text-sm md:text-base text-white/60 leading-relaxed max-w-md">
              {data.description}
            </p>
          </div>

          {/* RIGHT — accordion list */}
          <div className="lg:col-span-8" data-testid="faq-list">
            <ul className="border-t border-white/10">
              {data.items.map((item, i) => {
                const isOpen = openId === item.id;
                return (
                  <li
                    key={item.id}
                    data-testid={`faq-item-${item.id}`}
                    className="border-b border-white/10 group"
                  >
                    <button
                      type="button"
                      onClick={() => setOpenId(isOpen ? null : item.id)}
                      data-testid={`faq-toggle-${item.id}`}
                      className="w-full flex items-center justify-between gap-6 py-6 md:py-7 text-left"
                      aria-expanded={isOpen}
                    >
                      <div className="flex items-baseline gap-4 md:gap-6 min-w-0">
                        <span className="font-body text-[10px] uppercase tracking-[0.35em] text-accent/70 shrink-0">
                          {String(i + 1).padStart(2, "0")}
                        </span>
                        <span
                          className={`font-heading uppercase tracking-[0.06em] text-lg md:text-xl transition-colors ${
                            isOpen ? "text-accent" : "text-white group-hover:text-accent/80"
                          }`}
                        >
                          {item.q}
                        </span>
                      </div>
                      <motion.span
                        animate={{ rotate: isOpen ? 180 : 0 }}
                        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                        className={`flex items-center justify-center w-9 h-9 border transition-colors shrink-0 ${
                          isOpen ? "border-accent text-accent bg-accent/10" : "border-white/20 text-white/60"
                        }`}
                      >
                        {isOpen ? <Minus size={14} /> : <Plus size={14} />}
                      </motion.span>
                    </button>
                    <motion.div
                      initial={false}
                      animate={{
                        height: isOpen ? "auto" : 0,
                        opacity: isOpen ? 1 : 0,
                      }}
                      transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                      className="overflow-hidden"
                    >
                      <div className="pl-10 md:pl-16 pr-4 pb-7 md:pb-8 pt-1">
                        <p className="font-body text-sm md:text-base text-white/70 leading-relaxed max-w-2xl">
                          {item.a}
                        </p>
                      </div>
                    </motion.div>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
